import type { CaptureFileJSONObject } from '@ikegps/core-api';
import type { CollectionModel } from '@ikegps/core-shared';
import { CollectionModelFieldSubForm } from '@ikegps/core-shared';
import { CollectionModelFieldTrueSize } from '@ikegps/core-shared';
import { CollectionModelFieldImage } from '@ikegps/core-shared';
import { forEach } from 'lodash';
import { T, Translate } from '../../i18n';
import { Services } from '../../services';
import { DataGrid, DEFAULT_COLUMN_AMOUNT } from './components/DataGrid';
import type { ImageReference } from './components/ImageComponent';
import { ImageGrid } from './components/ImageGrid';
import { SubForm } from './components/SubForm';
import { DataExtractor } from './DataExtractor';

/**
 * Checks if a poles capture has any user measurements / annotations
 * NOTE: Insight generated measurements are ignored as we don't want them so show up in reports
 * @param pole The pole object.
 * @param captureId The ID of the capture.
 * @returns A boolean indicating whether the capture has user measurements / annotations
 */
export function hasUserMeasurement(pole: CollectionModel, captureId: string): boolean {
	return pole.getDrawableState(captureId).hasUserMeasurements();
}

/**
 * Checks if a capture is marked as hidden
 * @param pole The pole object.
 * @param captureId -he ID of the capture.
 * @returns A boolean indicating whether the capture is marked as hidden
 */
export function isCaptureHidden(pole: CollectionModel, captureId: string): boolean {
	return pole.getImageVisibilityState(captureId).hideForExport();
}

/**
 * Generates a composite URL for a given pole and capture file
 * @param pole The pole object.
 * @param capture The capture file object.
 * @returns The composite URL.
 */
export function compositeURL(pole: CollectionModel, capture: CaptureFileJSONObject) {
	const units = Services.UserSettings.units;
	return `${window.location.origin}/v1/collection/${pole.id}/${pole.revision}/${capture.objectId}_medium_composite_${units}.jpeg`;
}

/**
 * Generates an ImageReference string based on the provided pole and capture information.
 * @param pole The pole object.
 * @param capture The capture file JSON object.
 * @returns The generated ImageReference string
 */
export function captureReferenceString(pole: CollectionModel, capture: CaptureFileJSONObject): string {
	return `${pole.revision}-${capture.objectId}`;
}

/**
 * Extracts information from a sub form and returns an array of renderable SubForms.
 * @param subform The sub form to extract data from
 * @param imagesHolder The object to store image URL references
 * @returns An array of renderable SubForms.
 */
export function extractSubforms(
	pole: CollectionModel,
	subform: CollectionModelFieldSubForm,
	imagesHolder: Record<string, string>,
	columns = DEFAULT_COLUMN_AMOUNT
): SubForm[] {
	const instanceArray: SubForm[] = [];

	forEach(subform.instances, (instance) => {
		const subfieldsArray: [string, string][] = [];
		const subformArray: SubForm[] = [];
		const imageGrid: ImageGrid[] = [];
		const truesizeGrid: ImageGrid[] = [];

		instance.forEachField((subfield) => {
			if (subfield.parent.instanceId === instance.instanceId) {
				switch (true) {
					// Extract all the images and add them to their respective grids
					case subfield instanceof CollectionModelFieldImage:
					case subfield instanceof CollectionModelFieldTrueSize:
						const captures = subfield.getCaptures();
						const targetGrid = subfield instanceof CollectionModelFieldTrueSize ? truesizeGrid : imageGrid;
						const isTrueSizeSubfield = subfield instanceof CollectionModelFieldTrueSize;

						if (captures.length > 0) {
							const imageReferences: ImageReference[] = [];
							captures.forEach((capture) => {
								if (!isCaptureHidden(subform.collection, capture.objectId)) {
									const imageReferenceString = captureReferenceString(pole, capture);
									// For Image subfields, use composite if crop is set, otherwise use medium (with zoom)
									const hasCrop = subform.collection.getCropParameterState(capture.objectId).useInExportCorrections();
									const url =
										isTrueSizeSubfield || hasCrop ? compositeURL(subform.collection, capture) : capture.medium.url;
									imagesHolder[imageReferenceString] = url;
									imageReferences.push({ reference: imageReferenceString });
								}
							});

							if (imageReferences.length > 0) {
								targetGrid.push(new ImageGrid({ [subfield.schema.displayName]: imageReferences }));
							}
						}
						break;
					case subfield instanceof CollectionModelFieldSubForm:
						subformArray.push(...extractSubforms(pole, subfield, imagesHolder, columns));
						break;
					default:
						const extractedValue = DataExtractor.createPrintableValue(subfield);
						if (extractedValue.value) {
							subfieldsArray.push([extractedValue.title, extractedValue.value]);
						}
				}
			}
		});

		instanceArray.push(
			new SubForm(
				subform.schema.displayName,
				instance.title || instance.subtitle || Translate(T.COLLECTION_MISSING_TITLE),
				[new DataGrid(subfieldsArray, columns), ...imageGrid, ...truesizeGrid, ...subformArray]
			)
		);
	});

	return instanceArray;
}
